import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import {
    AwaitIterators,
    GetAnimationClip,
    PlayAnimation,
} from '../AnimationHelper';
import Renderable from '../../../Component/Renderable';
import CardComponent from '../../../Component/Level/Card';
import KeyCard from '../../../Prototype/Level/Card/KeyCard';
import { delay } from '../../../Utility/Task/TaskQueue';
import { StartAudio } from '../../Audio/AudioHelper';
import { AUDIO_WILDCARD_KEY, AUDIO_WILDCARD_LOCK_OPEN } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffect } from '../CardAnimationHelper';

interface ICardKeyUnlockMeta {
    system: CardSystem;
    sourceCard: KeyCard;
    targetCards: BaseCard[];
}

export class CardKeyUnlock extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardKeyUnlockMeta
    ): IterableIterator<number> {
        const lockCardFlips = [];

        // Move key card to animator.
        animator.addChild(meta.sourceCard);

        // Move lock cards to animator.
        for (let lockCard of meta.targetCards) {
            animator.addChild(lockCard);
        }

        yield* delay(100);

        // Flip cards over if not already.
        for (let lockCard of meta.targetCards) {
            if (lockCard.getComponent(CardComponent).faceUp === false) {
                lockCard.getComponent(CardComponent).faceUp = true;
                lockCardFlips.push(
                    PlayAnimation(
                        GetAnimationClip('CardFlip', lockCard, { faceUp: true })
                    )
                );
            }
        }
        yield* AwaitIterators(lockCardFlips);
        yield* delay(100);

        // Hide source key card.
        meta.sourceCard.getComponent(Renderable).visible = false;

        const keyUnlockDuration = 600;

        const keyCards = [];
        for (let lockCard of meta.targetCards) {
            // Create new key cards to animate.
            const keyCard = GameObject.Instantiate(KeyCard);
            animator.addChild(keyCard);
            keyCards.push(keyCard);

            const lockCardTransform = lockCard.getComponent(TransformComponent);
            const keyCardTransform = keyCard.getComponent(TransformComponent);

            const position = meta.sourceCard.getComponent(TransformComponent)
                .position;
            keyCardTransform.position.set(position.x, position.y);

            const tweenRotation = this.getTween(keyCardTransform);
            const tweenPosition = this.getTween(keyCardTransform.position);

            tweenRotation
                .to({ rotation: Math.PI * 4 }, keyUnlockDuration)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);
            tweenPosition
                .to(
                    {
                        x: lockCardTransform.position.x,
                        y: lockCardTransform.position.y,
                    },
                    keyUnlockDuration
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            StartAudio(
                meta.system.scene,
                AUDIO_WILDCARD_KEY,
                AudioTrackType.SFX
            );
        }

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        yield* delay(keyUnlockDuration + 100);

        StartAudio(
            meta.system.scene,
            AUDIO_WILDCARD_LOCK_OPEN,
            AudioTrackType.SFX
        );

        // Destroy key cards.
        for (let keyCard of keyCards) {
            keyCard.destroy();
        }

        // Fade out lock cards
        for (let lockCard of meta.targetCards) {
            const alphaTween = this.getTween(lockCard.getComponent(Renderable));
            alphaTween.to({ alpha: 0 }, 100).start(this.elapsedTime);

            showCardScoreEffect(meta.system.scene, lockCard);
        }

        yield* delay(100);
    }
}
